import { rootDir } from '@/common/utils/pathPrefix.util';
import CancellationError from '@/common/utils/errors/CancellationError';
import NetworkError from '@/common/utils/errors/NetworkError';
import JsonParseError from '@/common/utils/errors/JsonParseError';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import ValidationError from '@/common/utils/errors/ValidationError';
import InternalServerError from '@/common/utils/errors/InternalServerError';
import ServerError from '@/common/utils/errors/ServerError';
import { throttle } from 'lodash-es';
import bus from '@/common/utils/eventBus';
import {
  trackNetworkError,
  trackBackendError,
  trackFileUploadStart,
  trackFileUploadComplete
} from '@/common/telemetry';
import extractFileInfo from '@/common/utils/fileInfoExtractor';
import { useSlowRequestsStore } from '@/common/store/pinia/slowRequests';
import BaseHttpAdapter from '@/common/utils/baseHttpAdapter';

type Config = {
  headers: FetchHeaders | Headers & XAuthorizationHeaders,
  url: string;
  path: string;
  params: any;
  abortController?: AbortController;
}

type ResponseData = CommonObject | string;

export default class CreateFetch extends BaseHttpAdapter {
  constructUri(path: UriPath, rootPath: string, params: FetchRequestParams): QueryParamsString {
    let url = `${rootPath}${encodeURI(path)}`;
    if (params && Object.keys(params).length > 0) {
      const queryParams = Object.keys(params).reduce((result: string[], key: string) => {
        const param = params[key];
        if (param === undefined) {
          return result;
        }
        if (typeof params[key] === 'object') {
          return result.concat(`${key}=${encodeURIComponent(JSON.stringify(param))}`);
        }
        return result.concat(`${key}=${encodeURIComponent(param)}`);
      }, []);
      if (queryParams.length) {
        url += `?${queryParams.join('&')}`;
      }
    }
    return url;
  }

  async getData(response): Promise<ResponseData> {
    const text = await response.text();
    let body;
    const contentType: string = response.headers.get('content-type') ?? '';
    if (text.length && /application\/json/i.test(contentType)) {
      try {
        body = JSON.parse(text);
      } catch (err) {
        throw new JsonParseError(text, response.status);
      }
    } else {
      body = text;
    }
    return body;
  }

  makeRequest(path: UriPath, { rootPath = rootDir, params = {}, headers = {}, ...restOptions }): Promise<FetchResponse> {
    let data;
    rootPath = rootPath.replace(/\/$/, '');
    const url = this.constructUri(path, rootPath, params);

    const config: Config = {
      url,
      path,
      params,
      ...restOptions,
      headers: {
        ...this.defaultHeaders,
        ...headers,
        ...this.getAuthorizationHeaders(restOptions),
      }
    };

    return this.makeFetch(config)
      .catch((err) => {
        if (err.name === 'AbortError') {
          throw new CancellationError(err);
        }
        const networkError = new NetworkError(err);
        trackNetworkError(networkError, url, {
          request_method: restOptions.method,
          request_headers: config.headers
        });
        throw networkError;
      })
      .then(async (res) => {
        data = await this.getData(res);
        return this.authGuard<Response>(res, data, restOptions as AuthGuardParams);
      })
      .then((res) => this.reconfigurationHook<Response>(res))
      .then(async (response) => {
        if (response.status >= 200 && response.status < 400) {
          if (restOptions.method !== 'get') {
            bus.emit(data);
          }
          return Promise.resolve({ data, response, request: restOptions.body, params });
        }
        if (response.status >= 400 && response.status < 500) {
          const error = response.status === 404
            ? new NotFoundError(response, data)
            : new ValidationError(response, data);

          trackBackendError(error, response.status, url, data?.error?.err_code, {
            request_method: restOptions.method,
            response_data: data,
            error_category: '4xx'
          });

          throw error;
        }

        const serverError = new InternalServerError(response, data);
        trackBackendError(serverError, response.status, url, data?.error?.err_code, {
          request_method: restOptions.method,
          response_data: data,
          error_category: '5xx'
        });
        throw serverError;
      });
  }

  makeFetch({ url, headers, path, params, abortController, ...options }: Config): Promise<Response> {
    const requestController = this.createRequestController(abortController);

    return fetch(url, {
      mode: 'cors',
      credentials: 'same-origin',
      ...options,
      headers,
      signal: requestController.controller.signal
    }).finally(() => {
      this.removeController(requestController);
    });
  }

  get<T = any>(path: UriPath, params?: FetchRequestParams, options?: any): Promise<FetchResponse<T>> {
    const rootPath = (options?.rootPath || rootDir).replace(/\/$/, '');
    const url = this.constructUri(path, rootPath, params || {});
    const slowRequestsStore = useSlowRequestsStore();

    const stopRequestLog = slowRequestsStore.logSlowRequest({ url, method: 'GET' });

    return this.makeRequest(path, { ...options, params, method: 'get' })
      .then((result) => {
        stopRequestLog('completed');
        return result;
      })
      .catch((err) => {
        let status: 'canceled' | 'network_error' | 'failed' = 'failed';

        if (err.name === 'AbortError' || err instanceof CancellationError) {
          status = 'canceled';
        } else if (err instanceof NetworkError) {
          status = 'network_error';
        }

        stopRequestLog(status, err);

        throw err;
      });
  }

  post<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'post' });
  }

  put<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'put' });
  }

  patch<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'PATCH' });
  }

  delete<T = any>(path: UriPath, body: FetchRequestBody = {}, options?: any): Promise<FetchResponse<T>> {
    return this.makeRequest(path, { ...options, body: JSON.stringify(body), method: 'delete' });
  }

  upload(path: UriPath, body: (Document | XMLHttpRequestBodyInit | null), options: any, onProgress?: (process: ProgressEvent, xhr: XMLHttpRequest) => void): Promise<UploadResponse> {
    const rootPath: string = rootDir.replace(/\/$/, '');

    return new Promise((resolve, reject) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();
      xhr.responseType = 'text';
      xhr.open(options.method, rootPath + path);
      xhr.timeout = 0;

      const { fileName, fileSize } = extractFileInfo(body, options);
      const uploadStartTime = trackFileUploadStart({
        fileName,
        fileSize,
        metadata: {
          upload_path: path,
          request_method: options.method
        }
      });

      const headers: XAuthorizationHeaders & FetchHeaders = {
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers,
        ...this.getAuthorizationHeaders(options),
      };

      Object.keys(headers).forEach(key => xhr.setRequestHeader(key, headers[key]));

      xhr.onload = async (e: ProgressEvent) => {
        const response: XMLHttpRequest = e.target as XMLHttpRequest;

        const { responseText } = response;
        let responseBody: any = {};
        const contentType: string = response.getResponseHeader('content-type') ?? '';
        if (responseText.length && /application\/json/i.test(contentType)) {
          try {
            responseBody = JSON.parse(responseText);
          } catch (err) {
            reject(new JsonParseError(responseText, response.status));
          }
        }

        const responseAdapter: Response = {
          status: response.status,
          statusText: response.statusText,
          headers: {
            get: (name: string) => response.getResponseHeader(name)
          }
        } as Response;

        this.authGuard(responseAdapter, responseBody, options as AuthGuardParams).catch((err: Error) => reject(err));

        const result: UploadResponse = { data: responseBody, response };
        if (response.status >= 200 && response.status < 400) {
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: true,
            additionalProperties: {
              status_code: response.status,
              response_size: responseText.length
            }
          });
          bus.emit(responseBody);
          resolve(result);
        } else if (response.status >= 400 && response.status < 500) {
          const errorMessage = `HTTP ${response.status}`;
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: false,
            errorMessage,
            additionalProperties: {
              status_code: response.status,
              error_category: '4xx'
            }
          });
          reject(new ValidationError(response, responseBody));
        } else {
          const errorMessage = `HTTP ${response.status}`;
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: false,
            errorMessage,
            additionalProperties: {
              status_code: response.status,
              error_category: '5xx'
            }
          });
          reject(new ServerError(response, responseBody));
        }
      };

      let timeoutId: ReturnType<typeof setTimeout>;
      const onError = (err) => {
        clearTimeout(timeoutId);
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Network error',
          additionalProperties: {
            error_type: 'network_error'
          }
        });
        reject(new NetworkError(err));
      };

      const onAbort = (err) => {
        clearTimeout(timeoutId);
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Upload cancelled',
          additionalProperties: {
            error_type: 'cancellation'
          }
        });
        reject(new CancellationError(err));
      };

      xhr.onerror = onError;
      xhr.onabort = onAbort;

      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = throttle((progress: ProgressEvent) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            trackFileUploadComplete({
              startTime: uploadStartTime,
              fileName,
              fileSize,
              success: false,
              errorMessage: 'Upload timeout',
              additionalProperties: {
                error_type: 'timeout',
                timeout_ms: options.uploadTimeout || 60000
              }
            });
            onError(progress);
          }, options.uploadTimeout || 60000);
          onProgress(progress, xhr);
        }, 100);
        xhr.onloadend = () => {
          clearTimeout(timeoutId);
        };
      }

      xhr.send(body);
    });
  }
}
